// GH #1723. What the scan was matching FOR.
//
// Reachable from the scan label in ScanContextNav. Judging whether an engine got a
// match wrong is guesswork without the need in front of you, and the admin should
// not have to leave the comparison to read it.
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material'

import { FundingNeed } from '../interfaces/matchRunInterfaces'

const money = (value: number | null) =>
  value === null || value === undefined
    ? null
    : `$${Number(value).toLocaleString()}`

const date = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : null

const Section = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <Box mb={2}>
    <Typography variant="overline" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {children}
    </Typography>
  </Box>
)

export default function FundingNeedDialog({
  need,
  open,
  onClose,
}: {
  need: FundingNeed | null
  open: boolean
  onClose: () => void
}) {
  if (!need) return null

  const range = [money(need.min_amount), money(need.amount)]
    .filter(Boolean)
    .join(' – ')
  const window = [
    date(need.funding_confirm_by_start),
    date(need.funding_confirm_by_end),
  ]
    .filter(Boolean)
    .join(' → ')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {need.title || `Funding need ${need.id}`}
        <Typography variant="subtitle2" color="text.secondary">
          {need.org_name} · need {need.id}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {range ? <Chip size="small" label={range} /> : null}
          {window ? <Chip size="small" label={window} /> : null}
          {need.timing_criteria ? (
            <Chip size="small" label={need.timing_criteria} />
          ) : null}
          {need.is_archived ? (
            <Chip size="small" color="warning" label="archived" />
          ) : null}
          {need.use_need_context_only ? (
            <Chip size="small" variant="outlined" label="need context only" />
          ) : null}
          <Chip
            size="small"
            variant="outlined"
            label={
              need.accepts_gambling_proceeds
                ? 'accepts gambling proceeds'
                : 'no gambling proceeds'
            }
          />
          <Chip
            size="small"
            variant="outlined"
            label={
              need.accepts_international
                ? 'accepts international'
                : 'domestic funders only'
            }
          />
        </Box>

        <Section title="Description">{need.description}</Section>

        {need.profile_text ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Section title="Profile text — what the model actually reads">
              {need.profile_text}
            </Section>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
